import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Check } from "lucide-react";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const plans = [
    {
      id: "free",
      name: "Free",
      price: 0,
      description: "Perfect for testing and small projects",
      features: [
        "100 reflections/month",
        "1 agent",
        "Community support",
        "Basic analytics"
      ],
      cta: "Current Plan"
    },
    {
      id: "starter",
      name: "Starter",
      price: 29,
      description: "Essential features for growing projects",
      features: [
        "10,000 reflections/month",
        "5 agents",
        "Email support",
        "Advanced analytics",
        "API access"
      ],
      cta: "Upgrade to Starter"
    },
    {
      id: "pro",
      name: "Pro",
      price: 99,
      description: "Advanced features with higher limits and priority support for production use",
      features: [
        "100,000 reflections/month",
        "Unlimited agents",
        "Priority support",
        "Custom integrations",
        "Advanced security",
        "Team collaboration"
      ],
      cta: "Upgrade to Pro",
      popular: true
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: 499,
      description: "Full-scale deployment with dedicated support",
      features: [
        "Unlimited reflections",
        "Unlimited agents",
        "Dedicated support",
        "SLA guarantees",
        "Custom deployment",
        "Advanced compliance",
        "White-label options"
      ],
      cta: "Upgrade to Enterprise"
    }
  ];

  const handleUpgrade = async (plan) => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (plan === "free") return;

    setLoading(plan);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/payments/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
          body: JSON.stringify({ plan }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        alert("Failed to create checkout session");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      alert("Failed to start checkout");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-void">
      {/* Header */}
      <div className="border-b border-line-1 bg-surface px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold text-ink-100 mb-2">Pricing</h1>
          <p className="text-ink-400">
            Choose the plan that fits your needs. Upgrade or downgrade anytime.
          </p>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`border bg-surface p-6 relative ${
                plan.popular
                  ? "border-brand shadow-lg scale-105"
                  : "border-line-1"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand text-void px-3 py-1 text-xs font-bold">
                  MOST POPULAR
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-bold text-ink-100 mb-2">
                  {plan.name}
                </h3>
                <div className="mb-3">
                  <span className="text-4xl font-bold text-ink-100">
                    ${plan.price}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-ink-400 ml-2">/month</span>
                  )}
                </div>
                <p className="text-sm text-ink-400">{plan.description}</p>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start text-sm">
                    <Check
                      className="text-terminal mr-2 flex-shrink-0 mt-0.5"
                      size={16}
                    />
                    <span className="text-ink-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleUpgrade(plan.id)}
                disabled={loading === plan.id || plan.id === "free"}
                className={`w-full py-3 font-semibold transition-colors ${
                  plan.popular
                    ? "bg-brand text-void hover:bg-brand/90"
                    : "bg-surface-2 text-ink-200 hover:bg-surface-3 border border-line-1"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading === plan.id ? "Loading..." : plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-16 text-center">
          <p className="text-ink-400">
            All plans include 14-day money-back guarantee. Need a custom plan?{" "}
            <a href="mailto:support@mnemonicai.com" className="text-brand hover:underline">
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
